import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchProjects,
  createProject,
  deleteProject,
  fetchProject,
} from "@/store/projectsSlice";
import { useNotification } from "@/context/NotificationContext";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { SearchField } from "@/components/SearchField";
import * as projectsApi from "@/api/projects.api";
import type { Project, ApiKeyCreated } from "@/types";

interface ProjectForm {
  name: string;
  shortName: string;
  slug: string;
  description: string;
  featureFlagMatchMode: "allOf" | "anyOf";
}

export function Projects() {
  const { t } = useTranslation();
  const getErrorMessage = useApiErrorMessage();
  const dispatch = useAppDispatch();
  const {
    items: projects,
    current,
    loading,
  } = useAppSelector((s) => s.projects);
  const { notify } = useNotification();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);

  const { control, handleSubmit, reset } = useForm<ProjectForm>({
    defaultValues: {
      name: "",
      shortName: "",
      slug: "",
      description: "",
      featureFlagMatchMode: "allOf",
    },
  });

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );
  const totalApiKeys = projects.reduce(
    (sum, p) => sum + (p.apiKeys?.length || 0),
    0,
  );

  const onCreateSubmit = async (data: ProjectForm) => {
    try {
      await dispatch(createProject(data)).unwrap();
      notify(t("projects.created"), "success");
      setCreateOpen(false);
      reset();
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const openDetail = async (id: string) => {
    await dispatch(fetchProject(id));
    setCreatedKey(null);
    setDetailOpen(true);
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteProject(deleteTarget.id)).unwrap();
      notify(t("projects.deleted"), "success");
      setDetailOpen(false);
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteTarget(null);
  };

  const onCreateApiKey = async () => {
    if (!current || !newKeyName.trim()) return;
    try {
      const key = await projectsApi.createApiKey(current.id, {
        name: newKeyName,
      });
      setCreatedKey(key);
      setNewKeyName("");
      dispatch(fetchProject(current.id));
      notify(t("projects.apiKeys.created"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  const onDeleteApiKey = async (keyId: string) => {
    if (!current) return;
    try {
      await projectsApi.deleteApiKey(current.id, keyId);
      dispatch(fetchProject(current.id));
      notify(t("projects.apiKeys.deleted"), "success");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
  };

  return (
    <Box>
      <PageHeader
        title={t("projects.title")}
        subtitle={t("projects.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              reset();
              setCreateOpen(true);
            }}
            color="secondary"
          >
            {t("projects.create")}
          </Button>
        }
      />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("projects.totalProjects")}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                }}
              >
                {projects.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("projects.totalApiKeys")}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                }}
              >
                {totalApiKeys}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t("common.search")}
      />
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("common.loading")}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography
                sx={{
                  color: "text.secondary",
                }}
              >
                {search ? t("common.noResults") : t("projects.noProjects")}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filtered.map((project, idx) => (
                <ListItem
                  key={project.id}
                  divider={idx < filtered.length - 1}
                  sx={{ px: 3, py: 2, cursor: "pointer" }}
                  onClick={() => openDetail(project.id)}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {project.name}
                        </Typography>
                        <Chip
                          label={t(
                            `projects.matchModeTag.${project.featureFlagMatchMode}`,
                          )}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={t("projects.apiKeysCountShort", {
                            count: project.apiKeys?.length || 0,
                          })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={project.description}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(project);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onCreateSubmit)}>
          <DialogTitle>{t("projects.create")}</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: "8px !important",
            }}
          >
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("projects.name")}
                  required
                  fullWidth
                />
              )}
            />
            <Controller
              name="shortName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("projects.shortName")}
                  fullWidth
                />
              )}
            />
            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <TextField {...field} label={t("projects.slug")} fullWidth />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("projects.description")}
                  multiline
                  rows={2}
                  fullWidth
                />
              )}
            />
            <Controller
              name="featureFlagMatchMode"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t("projects.matchMode")}</InputLabel>
                  <Select {...field} label={t("projects.matchMode")}>
                    <MenuItem value="allOf">
                      {t("projects.matchModeAllOf")}
                    </MenuItem>
                    <MenuItem value="anyOf">
                      {t("projects.matchModeAnyOf")}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {t("common.create")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {current?.name}{" "}
          <Chip
            label={
              current
                ? t(`projects.matchModeTag.${current.featureFlagMatchMode}`)
                : ""
            }
            size="small"
            sx={{ ml: 1 }}
          />
        </DialogTitle>
        <DialogContent>
          {createdKey && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(createdKey.value);
                    notify(t("projects.apiKeys.copySuccess"), "success");
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              }
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {createdKey.value}
              </Typography>
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("projects.apiKeys.title")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder={t("projects.apiKeys.name")}
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
            />
            <Button
              variant="contained"
              size="small"
              onClick={onCreateApiKey}
              disabled={!newKeyName.trim()}
            >
              {t("projects.apiKeys.create")}
            </Button>
          </Box>
          <List dense>
            {current?.apiKeys?.map((key) => (
              <ListItem key={key.id} divider>
                <ListItemText
                  primary={key.name}
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {key.maskedValue}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteApiKey(key.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {(!current?.apiKeys || current.apiKeys.length === 0) && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("projects.apiKeys.noKeys")}
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => current && setDeleteTarget(current)}
          >
            {t("projects.delete")}
          </Button>
          <Button onClick={() => setDetailOpen(false)}>
            {t("common.back")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("projects.delete")}
        message={t("projects.deleteConfirm")}
        onConfirm={onDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
